import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://chronocal.io";

// Generic fallback meta (used when a page doesn't set its own).
const DEFAULT_TITLE = "ChronoCal - The Performance Calendar That Works With Your Biology";
const DEFAULT_DESCRIPTION =
  "ChronoCal is the world's first performance-based calendar that schedules your work when your brain is wired to win. Integrate your wearables, optimize your energy, and reclaim 15+ hours of peak productivity per week.";

const DEFAULT_OG_IMAGE =
  "https://chronocal.io/assets/images/ui/b5ba0db6-c39f-4016-9785-3b5fe0bbfaaa.png";

type ResolvedMeta = {
  title: string;
  description: string;
  canonicalUrl: string;
};

function readCurrentMeta(fallbackCanonicalUrl: string): ResolvedMeta {
  const title = (document.title || DEFAULT_TITLE).trim() || DEFAULT_TITLE;

  const description =
    document.querySelector('meta[name="description"]')?.getAttribute("content")?.trim() ||
    DEFAULT_DESCRIPTION;

  const canonicalUrl =
    document.querySelector('link[rel="canonical"]')?.getAttribute("href")?.trim() ||
    fallbackCanonicalUrl;

  return { title, description, canonicalUrl };
}

/**
 * Provides **generic** SEO meta tags globally and keeps OG/Twitter tags
 * in sync with the current page's <title> + meta description.
 *
 * Pages can override these by rendering their own <Helmet> tags.
 */
export function DefaultSeo() {
  const location = useLocation();

  const fallbackCanonicalUrl = useMemo(() => {
    // Intentionally ignore query params for canonical by default.
    const path = location.pathname.startsWith("/") ? location.pathname : `/${location.pathname}`;
    return `${SITE_URL}${path}`;
  }, [location.pathname]);

  const [resolved, setResolved] = useState<ResolvedMeta>(() =>
    readCurrentMeta(fallbackCanonicalUrl),
  );

  useEffect(() => {
    let rafId = 0;

    const sync = () => {
      const next = readCurrentMeta(fallbackCanonicalUrl);
      setResolved((prev) => {
        if (
          prev.title === next.title &&
          prev.description === next.description &&
          prev.canonicalUrl === next.canonicalUrl
        ) {
          return prev;
        }
        return next;
      });
    };

    // Sync immediately, then observe for Helmet mutations (title/meta updates can be async).
    sync();

    const observer = new MutationObserver(() => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(sync);
    });

    observer.observe(document.head, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [location.pathname, location.search, fallbackCanonicalUrl]);

  return (
    <Helmet>
      {/* Fallback title for pages that don't set their own */}
      <title>{DEFAULT_TITLE}</title>
      {/* Note: meta description is NOT set here to avoid duplicates.
          Pages should set their own via Helmet, and index.html has a fallback.
          This component only syncs OG/Twitter tags with the existing description. */}
      <link rel="canonical" href={fallbackCanonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={resolved.title} />
      <meta property="og:description" content={resolved.description} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:url" content={resolved.canonicalUrl} />
      <meta property="og:site_name" content="ChronoCal" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@getChronocal" />
      <meta name="twitter:creator" content="@getChronocal" />
      <meta name="twitter:title" content={resolved.title} />
      <meta name="twitter:description" content={resolved.description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
    </Helmet>
  );
}

